"use client";

import { ButtonOverride as Button } from "@/components/Buttons/ButtonOverride";
import CopyButton from "@/components/Buttons/CopyButton";
import ConfirmedTx from "@/components/ConfirmedTx";
import ChevronRight from "@/components/Image/Svg/ChevronRight";
import SwitchArrows from "@/components/Image/Svg/SwitchArrows";
import TokenSwapInput from "@/components/Input/SwapInput";
import WalletInput from "@/components/Input/WalletInput";
import DexRoute from "@/components/Swap/Aggregator/DexRoute";
import TokenSelect from "@/components/Tokens/TokenSelect";
import { config } from "@/config/index";
import yakRouterAbi from "@/constants/abi/yakRouterAbi";
import { supportedChainIds } from "@/constants/project-specific";
import { chainSettings, defaultNativeAddress, fallbackChainId, type ChainType } from "@/constants/chain-specific";
import type { RequiredAggregatorChainType, RequiredTokenListType } from "@/constants/project-specific";
import type { NativeTokenPrices } from "@/constants/project-specific/farms/types";
import { pinnedTokens } from "@/constants/project-specific/tokens";
import type { TokenConfigType as BaseTokenConfigType } from "@/constants/types/currency";
import { useSwapBestPath } from "@/hooks/swap/useSwapBestPath";
import { useAllowance } from "@/hooks/useAllowance";
import useTokenData from "@/hooks/useTokenData";
import { resolve } from "@/lib/dns-names";
import { getNativePrices } from "@/lib/price-data/geckoPrices";
import { getInitialToken } from "@/lib/swap/defaults";
import { explorerUrl } from "@/util/explorerLinks";
import { Alert, cn, Link, Tab, Tabs, useDisclosure } from "@heroui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { waitForTransactionReceipt } from "@wagmi/core";
import { useSearchParams, usePathname } from "next/navigation";
import { Fragment, useCallback, useEffect, useMemo, useState, type Key } from "react";
import toast from '@/util/toast';
import { erc20Abi, getAddress, isAddress, parseUnits, type Address } from "viem";
import { useConnection, useBalance, useChainId, useWaitForTransactionReceipt, useWriteContract } from "wagmi";
import { truncAddress } from "../../util/address";
import AggSettingsPopover from "@/components/Swap/Aggregator/AggSettingsPopover";
import { swapAggConfigAtom } from "@/config/atoms/swapAggConfigAtom";
import { useAtom } from "jotai";
import { importedTokensAtom } from "@/config/atoms/tokens/customTokens";
import { useSwapTax } from "../../hooks/swap/useSwapTax";
import { useSwapTaxOnTransfer } from "../../hooks/swap/useSwapTaxOnTransferToken";
import { chainAggregators, getDexFactoryByAdapterAddress } from "@/constants/chain-specific/aggregators";
import { useIsPremium } from '../../hooks/useIsPremium';
import WatchAccount from "@/components/Wallet/WatchAccount";
import FloatingTokens from '@/components/Swap/FloatingTokens';
import type React from "react";
import { useIsMobile } from "@/hooks/utility/useIsMobile";
import PriceTicker from '@/components/Swap/PriceTicker';
import { getTokenStats } from "@/lib/price-data/getTokenStats";
import { DEX_LIST } from "@/constants/project-specific/farms/dex";
import { swapTheme } from "@/constants/project-specific/theme/swap";
import { AnimatePresence, motion } from "motion/react";
import { LiquidityTab } from "@/components/Liquidity/LiquidityTab";
export const fetchCache = 'default-cache';
export const revalidate = 1800;
export interface PricingObject {
  nativePrices: NativeTokenPrices;
  tokenPrices: Record<Address, number>;
}

// Augment the TokenConfigType to include fees and mainLpDex
interface TokenConfigTypeWithFees extends BaseTokenConfigType {
  fees?: {
    buy?: number;
    sell?: number;
  };
  mainLpDex?: string;
}
type SwapInputProps = {
  tokenIn: TokenConfigTypeWithFees;
  tokenOut: TokenConfigTypeWithFees;
  amountIn: bigint;
  amountOut: bigint;
  slippage?: number;
};
export type SwapTabKey = 'swap' | 'liquidity' | 'send';
interface Props {
  inToken?: TokenConfigTypeWithFees;
  outToken?: TokenConfigTypeWithFees;
  fullWidth?: boolean;
  priceData?: PricingObject;
  /** Default tab to show - if not provided, will be derived from URL pathname */
  defaultTab?: SwapTabKey;
  /** Enable URL-based tab routing (updates URL when tab changes) */
  enableUrlRouting?: boolean;
  display?: {
    showTicker?: boolean;
    showPremium?: boolean;
    showBubbles?: boolean;
  };
  onTxConfirmed?: () => void;
}
const Aggregator = ({
  inToken,
  outToken,
  fullWidth,
  priceData,
  defaultTab,
  enableUrlRouting = true,
  display = {
    showTicker: true,
    showPremium: true,
    showBubbles: true
  },
  onTxConfirmed
}: Props) => {
  const {
    address,
    chainId
  } = useConnection();
  const {
    isPremium,
    premPercentage
  } = useIsPremium(address);
  const [aggConfigAtom] = useAtom(swapAggConfigAtom);
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Determine the active tab from URL pathname or props
  const getActiveTabFromPath = useCallback((path: string): SwapTabKey => {
    if (path.startsWith('/liquidity')) return 'liquidity';
    if (path.startsWith('/send')) return 'send';
    return 'swap';
  }, []);

  // Track active tab in state (synced with URL)
  const [activeTab, setActiveTab] = useState<SwapTabKey>(() => {
    if (defaultTab) return defaultTab;
    return getActiveTabFromPath(pathname);
  });

  // Track if liquidity tab has been visited - only load data after first visit
  // This prevents unnecessary data fetching when user is on swap/send tabs
  const [hasVisitedLiquidity, setHasVisitedLiquidity] = useState<boolean>(() => {
    // Initialize based on whether we're starting on the liquidity tab
    if (defaultTab === 'liquidity') return true;
    return getActiveTabFromPath(pathname) === 'liquidity';
  });

  // Sync activeTab when pathname changes (e.g., browser back/forward)
  useEffect(() => {
    if (!defaultTab) {
      const tabFromPath = getActiveTabFromPath(pathname);
      setActiveTab(tabFromPath);
      // Also mark liquidity as visited if navigating directly to it
      if (tabFromPath === 'liquidity') {
        setHasVisitedLiquidity(true);
      }
    }
  }, [pathname, defaultTab, getActiveTabFromPath]);

  // Handle tab selection change with native History API (no page reload)
  const handleTabChange = useCallback((key: Key) => {
    const newTab = key as SwapTabKey;
    setActiveTab(newTab);

    // Mark liquidity tab as visited when user navigates to it
    // This triggers lazy loading of liquidity data
    if (newTab === 'liquidity') {
      setHasVisitedLiquidity(true);
    }
    if (enableUrlRouting) {
      // Use native History API - updates URL without page reload
      let newPath: string;
      switch (newTab) {
        case 'swap':
          newPath = '/swap';
          break;
        case 'liquidity':
          newPath = '/liquidity';
          break;
        case 'send':
          newPath = '/send';
          break;
        default:
          newPath = '/swap';
      }
      window.history.pushState(null, '', newPath);
    }
  }, [enableUrlRouting]);
  const outputCurrency = searchParams.get("outputCurrency");
  const [tokenIn, setTokenIn] = useState<TokenConfigTypeWithFees | undefined>(inToken ?? getInitialToken(chainId as RequiredTokenListType).in as TokenConfigTypeWithFees);
  const [tokenOut, setTokenOut] = useState<TokenConfigTypeWithFees | undefined>(outToken ? outToken : getInitialToken(chainId as RequiredTokenListType).out as TokenConfigTypeWithFees);
  const [sendAddress, setSendAddress] = useState<string>("");
  const [resolvedAddress, setResolvedAddress] = useState<string>("");
  const [tokenSend, setTokenSend] = useState<TokenConfigTypeWithFees | undefined>(outToken ?? pinnedTokens[chainId && supportedChainIds.aggregator.includes(chainId) ? chainId as RequiredTokenListType : fallbackChainId as RequiredTokenListType].swapDefaultFrom[0] as TokenConfigTypeWithFees);
  const [nativePrices, setNativePrices] = useState<NativeTokenPrices | undefined>(undefined);
  const [swapChainId, setSwapChainId] = useState<ChainType | undefined>(fallbackChainId);
  const [tokenPrices, setTokenPrices] = useState<Record<Address, number> | undefined>(undefined);
  const [amountTokenIn, setAmountTokenIn] = useState<string>("");
  const [amountTokenSend, setAmountTokenSend] = useState<string>("");
  // Track raw bigint balance when user clicks "max" for precision-safe swaps
  const [rawMaxAmountIn, setRawMaxAmountIn] = useState<bigint | null>(null);
  const [rawMaxAmountSend, setRawMaxAmountSend] = useState<bigint | null>(null);
  const {
    isOpen,
    onOpenChange,
    onOpen
  } = useDisclosure();
  const {
    isOpen: isOutOpen,
    onOpenChange: onOutOpenChange,
    onOpen: onOutOpen
  } = useDisclosure();
  const {
    isOpen: isSendOpen,
    onOpenChange: onSendOpenChange,
    onOpen: onSendOpen
  } = useDisclosure();
  const {
    data: hash,
    writeContract,
    writeContractAsync,
    error,
    isPending,
    isError,
    status
  } = useWriteContract();
  const {
    data: balance,
    refetch: refreshBalance
  } = useBalance({
    address: address,
    chainId
  });
  const [importedTokens, setImportedTokens] = useAtom(importedTokensAtom);
  const basePairs = useMemo(() => {
    return [...pinnedTokens[chainId && supportedChainIds.aggregator.includes(chainId) ? chainId as RequiredTokenListType : fallbackChainId as RequiredTokenListType].basePairs, outToken ?? null, importedTokens?.[chainId && supportedChainIds.aggregator.includes(chainId) ? chainId as RequiredTokenListType : fallbackChainId as RequiredTokenListType] ?? []].filter(token => token).flat().filter((token_0): token_0 is TokenConfigTypeWithFees => token_0 !== null);
  }, [chainId, outToken, importedTokens]);
  const {
    tokenData,
    refresh
  } = useTokenData(basePairs, address, chainId && supportedChainIds.aggregator.includes(chainId) ? (chainSettings[chainId as RequiredAggregatorChainType] as any)?.yakRouter : (chainSettings[fallbackChainId as RequiredAggregatorChainType] as any)?.yakRouter, chainId);
  const [pendingApprove, setPendingApprove] = useState<boolean>(false);
  const handlePendingApprove = useCallback((isPendingApproval: boolean) => {
    setPendingApprove(isPendingApproval);
  }, []);
  const currentChainId = useChainId();
  const swapSlippage = 10;
  const queryClient = useQueryClient();
  const {
    quote
  } = useSwapBestPath({
    amountIn: parseUnits(amountTokenIn ?? "0", tokenIn?.decimals ?? 18) ?? BigInt(0),
    tokenIn: tokenIn?.address ? tokenIn.address as Address : "",
    tokenOut: tokenOut?.address as Address,
    gasPrice: 1000000000000,
    maxSteps: inToken?.fees ? 1 : aggConfigAtom.maxHops,
    chainId: chainId as ChainType ?? fallbackChainId
  });
  const {
    allowance,
    token: token_1,
    refresh: refreshAllowance
  } = useAllowance(tokenIn?.address as Address, quote.router, chainId as ChainType ?? fallbackChainId);
  const {
    token: sendTokenAllowance
  } = useAllowance(tokenSend?.address as Address, quote.router, chainId as ChainType ?? fallbackChainId);
  const {
    data: txReceipt,
    isPending: isReceiptPending,
    isError: isReceiptError,
    isFetched
  } = useWaitForTransactionReceipt({
    hash,
    chainId: chainId ?? fallbackChainId
  });
  useEffect(() => {
    if (!hash) return;
    onTxConfirmed?.();
    queryClient.invalidateQueries();
    refreshBalance();
    refreshAllowance();
  }, [hash, refreshBalance, refreshAllowance, queryClient.invalidateQueries, onTxConfirmed]);
  useEffect(() => {
    if (!currentChainId) return;
    if (currentChainId === swapChainId) return;
    if (chainId && !supportedChainIds.aggregator.includes(chainId)) return;
    setSwapChainId(currentChainId);
    setTokenIn(pinnedTokens[currentChainId as RequiredTokenListType].swapDefaultFrom[0]);
    if (!outToken) {
      setTokenOut(pinnedTokens[currentChainId as RequiredTokenListType].swapDefaultTo[0]);
    }
  }, [currentChainId, swapChainId, outToken]);

  // cache tokenStats

  const {
    data: tokenStats,
    isLoading,
    error: tokenStatsError
  } = useQuery({
    queryKey: ['tokenStats', chainId, basePairs],
    queryFn: () => getTokenStats(chainId ?? fallbackChainId, basePairs),
    enabled: !!chainId && !!basePairs.length,
    // Only run if chainId and basePairs are available
    staleTime: 1000 * 60 * 30,
    // 30 minutes
    retry: true,
    retryOnMount: true,
    retryDelay: attempt => Math.min(attempt > 1 ? 2 ** attempt * 10000 : 10000, 30 * 10000)
  });
  useEffect(() => {
    const fetchNativePrices = async () => {
      if (!chainId) return;
      if (priceData) {
        setNativePrices(priceData.nativePrices);
        setTokenPrices(priceData.tokenPrices);
        return;
      }
      if (tokenStats) {
        setTokenPrices(Object.fromEntries(Object.entries(tokenStats).map(([addr, stats]) => [addr, stats.price])));
      }
      const nativePrice = await getNativePrices([chainId]);
      setNativePrices(nativePrice);
      // console.log(nativePrice);
      // console.log("token prices: ", tokenStats);
    };
    fetchNativePrices();
  }, [chainId, basePairs, priceData, priceData?.nativePrices, priceData?.tokenPrices, tokenStats]);
  useEffect(() => {
    if (!isFetched) return;
    toast(<ConfirmedTx hash={txReceipt?.transactionHash} message="Transaction Confirmed!" />, {
      type: "success"
    });
    refreshBalance();
    queryClient.invalidateQueries();
  }, [isFetched, txReceipt?.transactionHash, queryClient, refreshBalance]);
  const handleTokenInChange = useCallback((token_2: TokenConfigTypeWithFees) => {
    setTokenIn(token_2);
    // Clear raw max when token changes
    setRawMaxAmountIn(null);
  }, []);
  const handleTokenSendChange = useCallback((token_3: TokenConfigTypeWithFees) => {
    setTokenSend(token_3);
    // Clear raw max when token changes
    setRawMaxAmountSend(null);
  }, []);
  const handleAmountTokenSendChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAmountTokenSend(e.target.value);
  }, []);

  // Handler for when user clicks "max" on swap input - stores raw bigint for precision
  const handleMaxClickIn = useCallback((rawBalance: string) => {
    try {
      setRawMaxAmountIn(BigInt(rawBalance));
    } catch {
      setRawMaxAmountIn(null);
    }
  }, []);

  // Handler for when user clicks "max" on send input - stores raw bigint for precision
  const handleMaxClickSend = useCallback((rawBalance_0: string) => {
    try {
      setRawMaxAmountSend(BigInt(rawBalance_0));
    } catch {
      setRawMaxAmountSend(null);
    }
  }, []);
  const handleSendAddressChange = useCallback(async (wallet: string | null) => {
    if (!wallet) return;
    if (wallet.startsWith("0x")) {
      setSendAddress(wallet);
    } else {
      try {
        const address_0 = await resolve(wallet, chainId);
        if (!address_0) {
          setResolvedAddress("");
        } else {
          setResolvedAddress(address_0);
          setSendAddress(address_0);
        }
      } catch (e_0) {
        setResolvedAddress("");
      }
    }
  }, []);
  const handleTokenOutChange = useCallback((token_4: TokenConfigTypeWithFees) => {
    setTokenOut(token_4);
  }, []);
  const handleSwitch = useCallback(() => {
    // Clear raw max when switching tokens since balance context changes
    setRawMaxAmountIn(null);
    if (tokenIn) {
      if (!tokenOut) {
        setTokenOut(tokenIn);
        setTokenIn(undefined);
        return;
      }
      setTokenOut(tokenIn);
    }
    if (tokenOut) {
      if (!tokenIn) {
        setTokenIn(tokenOut);
        setTokenOut(undefined);
        return;
      }
      setTokenIn(tokenOut);
      setAmountTokenIn(Number.parseFloat(quote.amountOut).toLocaleString("fullwide", {
        useGrouping: false
      }));
    }
  }, [tokenIn, tokenOut, quote.amountOut]);
  const {
    buyFee,
    sellFee,
    isTax
  } = useSwapTax({
    addresses: [tokenIn?.address === defaultNativeAddress || tokenIn?.address === chainSettings[chainId as RequiredAggregatorChainType ?? fallbackChainId as RequiredAggregatorChainType].wNative.address ? tokenOut?.address : tokenIn?.address, tokenOut?.address].filter(Boolean) as Address[],
    chainId,
    factoryAddress: (quote?.adapters?.[quote?.adapters?.length - 1] && getAddress(chainAggregators[chainId as ChainType ?? fallbackChainId]?.adapters?.[getAddress(quote?.adapters[quote?.adapters.length - 1] as string)].metadata?.factory?.address ?? "0xEDedDbde5ffA62545eDF97054edC11013ED72125")) ?? "0xEDedDbde5ffA62545eDF97054edC11013ED72125"
  });
  const {
    swapTokensForTokens,
    swapETHForTokens,
    swapTokensForETH,
    isPending: isTaxSwapPending,
    error: taxSwapError
  } = useSwapTaxOnTransfer(chainId ?? fallbackChainId);
  const handleSend = useCallback(() => {
    if (Number(amountTokenSend) > tokenData?.[tokenSend?.address as Address]?.balance?.formatted) {
      toast.error("Insufficient balance");
      return;
    }
    if (!sendAddress || !tokenSend || !amountTokenSend) {
      let address_1 = sendAddress;
      try {
        address_1 = getAddress(sendAddress);
      } catch (e_1) {
        toast.error("Invalid address");
        return;
      }
      if (!isAddress(address_1)) {
        toast.error("Invalid address");
        return;
      }
      toast.error("Please fill out all fields");
      return;
    }
    // CRITICAL: Use raw max amount if user clicked "max" for precision-safe sends
    // Only use rawMaxAmountSend if the displayed amount matches the formatted balance
    const formattedSendBalance = tokenData?.[tokenSend?.address as Address]?.balance?.formatted;
    const shouldUseRawSendMax = rawMaxAmountSend !== null && formattedSendBalance !== undefined && Math.abs(Number(amountTokenSend) - formattedSendBalance) < 0.0000001;
    const amountToSend = shouldUseRawSendMax ? rawMaxAmountSend : parseUnits(amountTokenSend, tokenSend.decimals);
    writeContract({
      address: tokenSend.address,
      abi: erc20Abi,
      chainId: chainId,
      functionName: "transfer",
      args: [getAddress(sendAddress), amountToSend]
    });
  }, [sendAddress, tokenSend, amountTokenSend, rawMaxAmountSend, writeContract, chainId, tokenData]);
  const handleSwap = useCallback(async () => {
    if (tokenIn && tokenOut && token_1?.decimals && amountTokenIn && quote && quote.amountOutBigInt && quote.path && quote.adapters && quote.router && Number(amountTokenIn) > 0) {
      // CRITICAL: Use raw max amount if user clicked "max" for precision-safe swaps
      // This prevents buffer underflow errors from smart contracts when dealing
      // with tokens that have many decimals
      let amountInBigInt: bigint;
      try {
        // Only use rawMaxAmountIn if the displayed amount matches the formatted balance
        // This indicates user clicked MAX and hasn't manually edited the value
        const formattedBalance = tokenData?.[tokenIn?.address as Address]?.balance?.formatted;
        const shouldUseRawMax = rawMaxAmountIn !== null && formattedBalance !== undefined && Math.abs(Number(amountTokenIn) - formattedBalance) < 0.0000001;
        amountInBigInt = shouldUseRawMax ? rawMaxAmountIn : parseUnits(amountTokenIn, token_1.decimals);
      } catch (error_0) {
        console.error('Failed to parse amount:', error_0);
        toast.error('Invalid amount entered');
        return;
      }

      // Validate the amount is positive
      if (amountInBigInt <= BigInt(0)) {
        toast.error('Amount must be greater than 0');
        return;
      }

      // Check if either token has a tax using the extended type
      const hasTax = isTax || tokenIn.fees?.sell !== undefined && tokenIn.fees.sell > 0 || tokenOut.fees?.buy !== undefined && tokenOut.fees.buy > 0;
      if (hasTax) {
        // For tax tokens, use the tax-supporting swap functions
        const minOut = quote.amountOutBigInt - quote.amountOutBigInt * BigInt(Math.floor((aggConfigAtom.slippageTolerance ?? swapSlippage) * 10)) / BigInt(1000);

        // First try to get DEX from token's mainLpDex property
        let dexKey = "chewyswap"; // Default fallback

        // Check if token has a mainLpDex property
        if (tokenIn.fees?.sell !== undefined && tokenIn.fees.sell > 0 && tokenIn.mainLpDex) {
          dexKey = tokenIn.mainLpDex;
        } else if (tokenOut.fees?.buy !== undefined && tokenOut.fees.buy > 0 && tokenOut.mainLpDex) {
          dexKey = tokenOut.mainLpDex;
        } else if (quote.adapters && quote.adapters.length > 0) {
          // Fallback to using the last adapter's DEX
          console.warn("adapter: ", quote.adapters[quote.adapters.length - 1]);
          const lastAdapter = quote.adapters[quote.adapters.length - 1];
          const adapterInfo = chainAggregators[chainId as ChainType ?? fallbackChainId]?.adapters?.[getAddress(lastAdapter as string)];
          if (adapterInfo?.metadata?.slug) {
            dexKey = adapterInfo.metadata.slug;
          }
        }

        // console.log(`Using ${dexKey} for tax token swap`);

        // Define path for tax swap
        const path_0: Address[] = [];
        if (tokenIn && tokenOut) {
          if (tokenIn.address === defaultNativeAddress) {
            // Native -> Token
            path_0.push(chainSettings[chainId as ChainType ?? fallbackChainId].wNative.address as Address);
            path_0.push(tokenOut.address as Address);
          } else if (tokenOut.address === defaultNativeAddress) {
            // Token -> Native
            path_0.push(tokenIn.address as Address);
            path_0.push(chainSettings[chainId as ChainType ?? fallbackChainId].wNative.address as Address);
          } else {
            // Token -> Token
            path_0.push(tokenIn.address as Address);
            path_0.push(tokenOut.address as Address);
          }
        }
        try {
          // Make sure DEX exists in the list
          if (!DEX_LIST[dexKey]) {
            console.warn(`DEX ${dexKey} not found in DEX_LIST, falling back to chewyswap`);
            dexKey = "chewyswap";
          }

          // Handle approval first if needed for non-native token
          if (tokenIn.address !== defaultNativeAddress) {
            if (allowance.bigInt < amountInBigInt) {
              handlePendingApprove(true);

              // Get router address for approval from DEX_LIST
              const dexRouterAddress = DEX_LIST[dexKey]?.router?.address;
              const txHash = await writeContractAsync({
                address: tokenIn.address,
                abi: erc20Abi,
                chainId: chainId,
                functionName: "approve",
                args: [dexRouterAddress, amountInBigInt]
              });
              const transactionReceipt = await waitForTransactionReceipt(config, {
                hash: txHash
              });
              toast(<ConfirmedTx hash={transactionReceipt.transactionHash} message="Approval Confirmed!" />, {
                type: "success"
              });
              handlePendingApprove(false);
            }
          }

          // Execute tax-aware swap
          let txHash_0: string | undefined;
          if (tokenIn.address === defaultNativeAddress) {
            // Native -> Token
            txHash_0 = await swapETHForTokens({
              path: path_0,
              amountIn: amountInBigInt,
              amountOutMin: minOut,
              dexKey
            });
          } else if (tokenOut.address === defaultNativeAddress) {
            // Token -> Native
            txHash_0 = await swapTokensForETH({
              path: path_0,
              amountIn: amountInBigInt,
              amountOutMin: minOut,
              dexKey
            });
          } else {
            // Token -> Token
            txHash_0 = await swapTokensForTokens({
              path: path_0,
              amountIn: amountInBigInt,
              amountOutMin: minOut,
              dexKey
            });
          }

          // Handle success
          if (txHash_0) {
            toast.success("Transaction sent! Waiting for confirmation...");
            queryClient.invalidateQueries();
          }
        } catch (error_1) {
          console.error("Tax token swap error:", error_1);
          toast.error("Swap failed: " + (error_1 instanceof Error ? error_1.message : "Unknown error"));
        }
        return;
      }

      // Regular non-tax token swap flow
      if (tokenIn.address !== defaultNativeAddress) {
        if (allowance.bigInt < amountInBigInt) {
          handlePendingApprove(true);
          const txHash_1 = await writeContractAsync({
            address: tokenIn.address as Address,
            abi: erc20Abi,
            chainId: chainId,
            functionName: "approve",
            args: [quote?.router, amountInBigInt]
          });
          const transactionReceipt_0 = await waitForTransactionReceipt(config, {
            hash: txHash_1
          });
          toast(<ConfirmedTx hash={transactionReceipt_0.transactionHash} message="Transaction Confirmed!" />, {
            type: "success"
          });
          handlePendingApprove(false);
        }
        swap(tokenIn, tokenOut, amountInBigInt, quote?.amountOutBigInt, swapSlippage, isPremium ? BigInt(0) : premPercentage > 0 ? BigInt(Math.floor(20 * (100 - premPercentage) / 100)) : BigInt(20));
        return;
      }
      swap(tokenIn, tokenOut, amountInBigInt, quote?.amountOutBigInt, swapSlippage, isPremium ? BigInt(0) : premPercentage ? BigInt(Math.floor(20 * (100 - premPercentage) / 100)) : BigInt(20));
    }
  }, [tokenIn, tokenOut, token_1, amountTokenIn, quote, swapSlippage, isTax, allowance, rawMaxAmountIn, tokenData, handlePendingApprove, swapTokensForTokens, swapETHForTokens, swapTokensForETH, chainId, writeContractAsync, aggConfigAtom.slippageTolerance, isPremium, premPercentage, queryClient]);
  const swap = useCallback(async (tokenIn_0: TokenConfigTypeWithFees, tokenOut_0: TokenConfigTypeWithFees, amountIn: bigint, amountOut: bigint, slippage = 5, fee: bigint = BigInt(25)) => {
    const minOut_0 = amountOut - amountOut * BigInt(Math.floor((aggConfigAtom.slippageTolerance ?? slippage) * 10)) / BigInt(1000);
    if (!amountIn || !address || !quote || !quote.router || !quote.path || !quote.adapters || !quote.amounts) {
      return;
    }
    if (tokenIn_0.address === defaultNativeAddress) {
      writeContract({
        address: quote.router,
        abi: yakRouterAbi,
        chainId: chainId,
        functionName: "swapNoSplitFromAVAX",
        args: [{
          amountIn,
          amountOut: minOut_0,
          path: quote.path,
          adapters: quote.adapters
        }, address, fee],
        value: amountIn
      });
    } else if (tokenOut_0.address === defaultNativeAddress) {
      writeContract({
        address: quote.router,
        abi: yakRouterAbi,
        chainId: chainId,
        functionName: "swapNoSplitToAVAX",
        args: [{
          amountIn,
          amountOut: minOut_0,
          path: quote.path,
          adapters: quote.adapters
        }, address, fee]
      });
    } else {
      writeContract({
        address: quote.router,
        abi: yakRouterAbi,
        chainId: chainId,
        functionName: "swapNoSplit",
        args: [{
          amountIn,
          amountOut: minOut_0,
          path: quote.path,
          adapters: quote.adapters
        }, address, fee]
      });
    }
  }, [address, chainId, quote, quote.router, quote.path, quote.adapters, quote.amounts, writeContract, aggConfigAtom.slippageTolerance]);
  const getButtonText = useMemo(() => {
    if (quote?.error) {
      return "Error";
    }
    if (isPending || isTaxSwapPending) {
      return "Confirm in Wallet...";
    }
    if (Number(amountTokenIn) > tokenData?.[tokenIn?.address as Address]?.balance?.formatted) {
      return "Insufficient Balance";
    }
    // Check approval status - wrap parseUnits in try-catch for safety
    if (tokenIn && tokenIn.address !== defaultNativeAddress && amountTokenIn && token_1?.decimals !== undefined) {
      try {
        const parsedAmount = parseUnits(amountTokenIn, token_1.decimals);
        if (allowance.bigInt < parsedAmount) {
          return "Approve";
        }
      } catch {
        // Invalid amount format, will be caught during swap
      }
    }
    if (pendingApprove) {
      return "Approving...";
    }

    // Check for tax token with the extended type
    if (isTax || tokenIn?.fees?.sell !== undefined && tokenIn.fees.sell > 0 || tokenOut?.fees?.buy !== undefined && tokenOut.fees.buy > 0) {
      return "Swap (Tax Token)";
    }
    return "Swap";
  }, [quote?.error, isPending, isTaxSwapPending, tokenIn, tokenOut, allowance, amountTokenIn, token_1, pendingApprove, isTax, tokenData?.[tokenIn?.address as Address]?.balance?.formatted]);
  const buttonDisabled = useMemo(() => {
    if (quote?.error) {
      return true;
    }
    if (isPending || isTaxSwapPending) {
      return true;
    }
    if (!tokenIn || !tokenOut || !amountTokenIn) {
      return true;
    }
    if (Number(amountTokenIn) > tokenData?.[tokenIn?.address as Address]?.balance?.formatted) {
      return true;
    }
    return false;
  }, [quote?.error, isPending, isTaxSwapPending, tokenIn, tokenOut, amountTokenIn, tokenData]);
  const handleFloatingTokenSelect = (token_5: TokenConfigTypeWithFees) => {
    if (!tokenIn) {
      setTokenIn(token_5);
      return;
    }
    if (!tokenOut) {
      setTokenOut(token_5);
      return;
    }
    setTokenOut(token_5);
  };
  const isMobile = useIsMobile();
  return <div className="relative w-full flex flex-col items-center justify-center">
      {!isMobile && display.showBubbles ? <FloatingTokens chainId={chainId && supportedChainIds.aggregator.includes(chainId) ? chainId as RequiredTokenListType : fallbackChainId as RequiredTokenListType} tokenPrices={tokenPrices} tokenStats={tokenStats} onTokenSelect={handleFloatingTokenSelect} /> : null}
      {display.showTicker ? <PriceTicker chainId={chainId && supportedChainIds.aggregator.includes(chainId) ? chainId as RequiredTokenListType : fallbackChainId as RequiredTokenListType} tokenPrices={tokenPrices} tokenStats={tokenStats} onTokenSelect={handleFloatingTokenSelect} /> : null}
      <div className={cn(!fullWidth && "md:w-1/3 pt-5 max-w-fit self-center", "flex flex-col justify-center w-lvw md:w-full mx-2 md:mx-auto")}>
        <WatchAccount />
        <div className="relative flex w-full top-10 right-3 justify-end align-baseline">
          <AggSettingsPopover />
        </div>
        <Tabs aria-label="DEX Aggregator" className="flex w-full justify-center self-center" variant="underlined" destroyInactiveTabPanel={false} selectedKey={activeTab} onSelectionChange={handleTabChange}>
          <Tab key="swap" title="Swap">
            <div className="flex flex-col self-center w-full">
              <TokenSelect isOpen={isOpen} onOpenChange={() => onOpenChange()} onChange={handleTokenInChange} label="Select In Token" tokens={basePairs} account={address} chain={chainId ?? fallbackChainId} tokenData={tokenData} />
              <TokenSelect isOpen={isOutOpen} onOpenChange={() => onOutOpenChange()} onChange={handleTokenOutChange} label="Select Out Token" tokens={basePairs} account={address} chain={chainId ?? fallbackChainId} tokenData={tokenData} />

              <div className="flex flex-col self-center w-full">
                <TokenSwapInput chainId={chainId as RequiredAggregatorChainType} nativePrices={nativePrices && nativePrices} label={"Sell"} onSelectClick={() => onOpen()} balance={Number(tokenData?.[tokenIn?.address as Address]?.balance?.formatted ? tokenData?.[tokenIn?.address as Address]?.balance?.formatted ?? 0 : 0)} rawBalance={tokenData?.[tokenIn?.address as Address]?.balance?.bigInt?.toString()} selectedToken={tokenIn} onChange={e_2 => {
                setAmountTokenIn(e_2.target.value);
              }} onMaxClick={handleMaxClickIn} value={amountTokenIn} tokenPrices={tokenPrices && tokenPrices} />
                <div className="flex h-fit align-middle justify-center self-center">
                  <Button size="lg" className="absolute align-middle justify-center self-center transition-all duration-200 bg-clip-padding hover:rotate-180 border-4" onPress={handleSwitch} isIconOnly radius="full">
                    <SwitchArrows width={50} />
                  </Button>
                </div>
                <TokenSwapInput chainId={chainId as RequiredAggregatorChainType ?? fallbackChainId as RequiredAggregatorChainType} balance={Number(tokenData?.[tokenOut?.address as Address]?.balance?.formatted ? tokenData?.[tokenOut?.address as Address]?.balance?.formatted ?? 0 : 0)} nativePrices={nativePrices && nativePrices} type={"Buy"} onSelectClick={() => onOutOpen()} selectedToken={tokenOut} value={quote?.amountOut} tokenPrices={tokenPrices && tokenPrices} />
              </div>
              <div className="flex flex-col justify-center w-full content-center">
                <div className="flex flex-col justify-center self-center w-full">
                  <Button radius="sm" color="primary" size="lg" className="mt-2 w-full" onPress={() => handleSwap()} isLoading={isPending} isDisabled={buttonDisabled}>
                    <AnimatePresence mode="sync" initial={false}>
                      <motion.div key={getButtonText} style={{
                      textWrap: "nowrap"
                    }} initial={{
                      y: -20,
                      opacity: 0,
                      filter: "blur(10px)",
                      position: "absolute"
                    }} animate={{
                      y: 0,
                      opacity: 1,
                      filter: "blur(0px)",
                      position: "relative"
                    }} exit={{
                      y: 20,
                      opacity: 0,
                      filter: "blur(10px)",
                      position: "absolute"
                    }} transition={{
                      duration: 0.2,
                      ease: "easeInOut"
                    }}>{getButtonText}
                      </motion.div>
                    </AnimatePresence>
                  </Button>
                  {tokenIn && tokenOut && tokenPrices && amountTokenIn && !Number.isNaN(nativePrices?.[Number(chainId)]?.priceUsd) && !Number.isNaN(tokenPrices?.[tokenIn?.address]) && !Number.isNaN(tokenPrices?.[tokenOut?.address]) && <span className="text-center pt-3">
                      </span>}

                  {Array.isArray(quote.adapters) && quote.path && <div className="flex flex-wrap justify-center pt-6">
                      {quote.adapters.map((adapter: Address, index: number) => <Fragment key={adapter}>
                          <DexRoute address={adapter} chainId={chainId ?? fallbackChainId} pathIndex={index} tokenPath={quote.path} />
                          {index < (quote?.adapters?.length ?? 0) - 1 ? <ChevronRight className="block" width={20} /> : null}
                        </Fragment>)}
                    </div>}
                  {display.showPremium && isPremium && !isTax || premPercentage > 0 && display.showPremium ? <div className="flex flex-col justify-center place-self-center pt-6">
                      <Alert variant="flat" color="success" title="CHEWY Holder" description={`You are a ${isPremium ? 'premium user' : 'CHEWY holder'} and get a ${!isPremium && premPercentage > 0 ? Math.floor(premPercentage) : isPremium ? "100" : "0"}% discount on convenience fee! ${premPercentage > 0 ? "Buy more CHEWY for bigger discount!" : ""}`} className="p-0" />
                    </div> : <div className="flex flex-col justify-center place-self-center text-sm pt-6">
                    {isTax ? '* Single Hop Only' : '* Hold CHEWY to get a discount on 0.2% fee. 6900 CHEWY = NO Fee!'}
                  </div>}
                  {isTax && <div className="flex flex-col justify-center place-self-center pt-6">
                      <Alert variant="flat" color="warning" title={`${tokenIn?.address === defaultNativeAddress || tokenIn?.address === chainSettings[chainId as RequiredAggregatorChainType ?? fallbackChainId as RequiredAggregatorChainType].wNative.address ? tokenOut?.symbol : tokenIn?.symbol} has a tax:`} description={`${buyFee && buyFee > 0 ? ` Buy Fee: ${buyFee}%` : ""}${sellFee && sellFee > 0 ? ` Sell Fee: ${sellFee}%` : ""}`} className="p-0" />
                    </div>}
                </div>
              </div>
            </div>
          </Tab>
          <Tab key="liquidity" title="Liquidity" className="w-full">
            {/* Only render LiquidityTab after user first navigates to this tab
                This prevents unnecessary data fetching when on swap/send tabs
                Once visited, state is preserved due to destroyInactiveTabPanel={false} */}
            {hasVisitedLiquidity ? <LiquidityTab chainId={chainId as ChainType} className="w-full" useProgressiveLoading={true} /> : null}
          </Tab>
          <Tab key="send" title="Send">
            <div className="flex flex-col self-center w-full">
              <TokenSelect isOpen={isSendOpen} onOpenChange={() => onSendOpenChange()} onChange={handleTokenSendChange} label="Select Token" tokens={basePairs} account={address} chain={chainId ?? fallbackChainId} tokenData={tokenData} />
              <TokenSwapInput chainId={chainId as RequiredAggregatorChainType} nativePrices={nativePrices && nativePrices} onSelectClick={() => onSendOpen()} balance={Number(tokenData?.[tokenSend?.address as Address]?.balance?.formatted ? tokenData?.[tokenSend?.address as Address]?.balance?.formatted ?? 0 : 0)} rawBalance={tokenData?.[tokenSend?.address as Address]?.balance?.bigInt?.toString()} label="Send" selectedToken={tokenSend} onChange={handleAmountTokenSendChange} onMaxClick={handleMaxClickSend} value={amountTokenSend} tokenPrices={tokenPrices && tokenPrices} />
              <WalletInput onChange={handleSendAddressChange} type="To Address" chainId={chainId ?? fallbackChainId} />
              {resolvedAddress && <div className="flex flex-col justify-center content-center p-3 rounded-lg border-2 transition-colors bg-slate-900/50 w-full">
                  <div className="flex flex-row justify-between">
                    <div className="text-sm w-full place-self-center">
                      Resolved Address:{" "}
                      <span className="align-middle text-right text-sm text-secondary w-full">
                        <Link isExternal showAnchorIcon size="sm" href={`${explorerUrl(chainId ?? fallbackChainId)}/address/${resolvedAddress}`} color="secondary">
                          {truncAddress(resolvedAddress)}
                        </Link>
                      </span>
                    </div>
                    <div className="self-center">
                      <CopyButton text={resolvedAddress} size="sm" color="secondary" />
                    </div>
                  </div>
                </div>}

              <Button className="w-full mt-2" size="lg" radius="sm" color="primary" onPress={() => handleSend()} isLoading={isPending} isDisabled={isPending || !sendAddress || !tokenSend || !amountTokenSend}>
                {isPending ? "Confirm in Wallet..." : "Send"}
              </Button>
            </div>
          </Tab>
        </Tabs>
      </div>
    </div>;
};
export default Aggregator;